import React, { useEffect, useState } from "react";
import {
  FaAddressBook,
  FaCalendarAlt,
  FaEye,
  FaMousePointer,
  FaPercent,
  FaPlayCircle,
  FaRupeeSign,
  FaTag,
  FaUserPlus,
  FaUsers,
  FaWallet,
} from "react-icons/fa";
import { ErrorBox, Stat } from "./common";
import { errorMessage, fmtMoney, fmtNum, toISODate } from "../dmApi";

function defaultRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 29);
  return { date_from: toISODate(from), date_to: toISODate(to) };
}

/** Spend, leads, cost per lead and per-platform breakdown for a date range. */
function ProjectSummary({ api, projectId, refreshKey, compact = false, embedded = false }) {
  const [range, setRange] = useState(defaultRange);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api
      .summary(projectId, range)
      .then((data) => {
        if (cancelled) return;
        setSummary(data);
        setError("");
      })
      .catch((err) => {
        if (!cancelled) setError(errorMessage(err, "Could not load summary"));
      });
    return () => {
      cancelled = true;
    };
  }, [api, projectId, range, refreshKey]);

  const t = summary?.in_range || {};
  return (
    <div className={embedded ? "dmp-results" : "dmp-card dmp-results"}>
      <div className="dmp-results__head">
        {embedded ? null : <h3 className="dmp-results__title">Results</h3>}
        <div className="dmp-results__bar">
          {compact && summary ? (
            <div className="dmp-chips dmp-results__chips">
              {Object.entries(summary.content || {}).map(([status, count]) => (
                <span key={status} className="dmp-pill">
                  {status} <strong>{count}</strong>
                </span>
              ))}
              <span className="dmp-pill dmp-pill--accent">
                Campaigns <strong>{summary.campaigns?.active ?? 0}</strong> active / {summary.campaigns?.total ?? 0}
              </span>
            </div>
          ) : null}
          <div className="dmp-range">
            <FaCalendarAlt aria-hidden="true" className="dmp-range__icon" />
            <input
              type="date"
              value={range.date_from}
              aria-label="From date"
              onChange={(e) => setRange((r) => ({ ...r, date_from: e.target.value }))}
            />
            <span className="dmp-range__sep">to</span>
            <input
              type="date"
              value={range.date_to}
              aria-label="To date"
              onChange={(e) => setRange((r) => ({ ...r, date_to: e.target.value }))}
            />
          </div>
        </div>
      </div>
      <ErrorBox message={error} />
      <div className="dmp-stats">
        <Stat icon={FaEye} tone="blue" label="Impressions" value={fmtNum(t.Impressions)} />
        <Stat icon={FaUsers} tone="indigo" label="Reach" value={fmtNum(t.Reach)} />
        <Stat icon={FaPlayCircle} tone="violet" label="Views" value={fmtNum(t.Views)} />
        <Stat icon={FaMousePointer} tone="cyan" label="Clicks" value={fmtNum(t.Clicks)} />
        <Stat icon={FaPercent} tone="teal" label="CTR" value={t.CTR != null ? `${t.CTR}%` : "—"} />
        <Stat icon={FaUserPlus} tone="green" label="Leads (reported)" value={fmtNum(t.Leads)} />
        <Stat icon={FaAddressBook} tone="green" label="Leads in CRM" value={fmtNum(summary?.crm_leads)} />
        <Stat icon={FaRupeeSign} tone="amber" label="Spend" value={fmtMoney(t.Spend)} />
        <Stat
          icon={FaTag}
          tone="amber"
          label="Cost per lead"
          value={t.Cost_Per_Lead != null ? fmtMoney(t.Cost_Per_Lead) : "—"}
        />
        <Stat
          icon={FaWallet}
          tone="rose"
          label="Active budget / day"
          value={fmtMoney(summary?.campaigns?.active_budget_per_day)}
        />
      </div>

      {!compact && (summary?.by_platform || []).length > 0 ? (
        <div className="dmp-table-wrap">
          <table className="dmp-table">
            <thead>
              <tr>
                <th>Platform</th>
                <th className="dmp-num">Impressions</th>
                <th className="dmp-num">Reach</th>
                <th className="dmp-num">Views</th>
                <th className="dmp-num">Clicks</th>
                <th className="dmp-num">Likes</th>
                <th className="dmp-num">Shares</th>
                <th className="dmp-num">Leads</th>
                <th className="dmp-num">Spend</th>
                <th className="dmp-num">CPL</th>
              </tr>
            </thead>
            <tbody>
              {summary.by_platform.map((row) => (
                <tr key={row.Platform}>
                  <td>{row.Platform}</td>
                  <td className="dmp-num">{fmtNum(row.Impressions)}</td>
                  <td className="dmp-num">{fmtNum(row.Reach)}</td>
                  <td className="dmp-num">{fmtNum(row.Views)}</td>
                  <td className="dmp-num">{fmtNum(row.Clicks)}</td>
                  <td className="dmp-num">{fmtNum(row.Likes)}</td>
                  <td className="dmp-num">{fmtNum(row.Shares)}</td>
                  <td className="dmp-num">{fmtNum(row.Leads)}</td>
                  <td className="dmp-num">{fmtMoney(row.Spend)}</td>
                  <td className="dmp-num">{row.Cost_Per_Lead != null ? fmtMoney(row.Cost_Per_Lead) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {!compact && (summary?.by_campaign || []).length > 0 ? (
        <div className="dmp-table-wrap" style={{ marginTop: 12 }}>
          <table className="dmp-table">
            <thead>
              <tr>
                <th>Campaign</th>
                <th className="dmp-num">Impressions</th>
                <th className="dmp-num">Clicks</th>
                <th className="dmp-num">Leads</th>
                <th className="dmp-num">Spend</th>
                <th className="dmp-num">CPL</th>
              </tr>
            </thead>
            <tbody>
              {summary.by_campaign.map((row) => (
                <tr key={row.Campaign_Id}>
                  <td>
                    {row.Campaign_Name}
                    <div className="dmp-muted" style={{ fontSize: 12 }}>
                      {row.Platform}
                      {row.Linked ? " · synced from ad account" : ""}
                    </div>
                  </td>
                  <td className="dmp-num">{fmtNum(row.Impressions)}</td>
                  <td className="dmp-num">{fmtNum(row.Clicks)}</td>
                  <td className="dmp-num">{fmtNum(row.Leads)}</td>
                  <td className="dmp-num">{fmtMoney(row.Spend)}</td>
                  <td className="dmp-num">{row.Cost_Per_Lead != null ? fmtMoney(row.Cost_Per_Lead) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

export default ProjectSummary;
